import { Alert, Button, Divider, Modal, Space, Typography } from 'antd';
import { isCustomerServiceEnabledSync } from '../components/CustomerService/availability';
import NodeStatusModal from '../components/Header/NodeStatusModal';
import React, { useState } from 'react';

const { Text } = Typography;

export const UPLOAD_ERROR_PROMPT_CONFIG_URL = '/config/upload-error-prompt';
export const DEFAULT_RECOVERY_DOMAIN_STATUS_CODES = [520, 521, 522, 524];

interface ApiResponse<T> {
  success?: boolean;
  isSuccess?: boolean;
  data?: T;
  detail?: T;
  message?: string;
}

interface UploadErrorPromptConfig {
  recoveryDomainStatusCodes?: unknown;
}

const DEFAULT_RECOVERY_DOMAIN_STATUS_CODE_SET = new Set(DEFAULT_RECOVERY_DOMAIN_STATUS_CODES);
const UPLOAD_ERROR_PROMPT_CACHE_TTL_MS = 5000;
const UPLOAD_ERROR_PROMPT_FETCH_TIMEOUT_MS = 800;

let cachedRecoveryDomainStatusCodes: Set<number> | null = null;
let cachedRecoveryDomainStatusCodesExpiresAt = 0;
let inflightUploadErrorPromptRequest: Promise<Set<number>> | null = null;

const getDefaultRecoveryDomainStatusCodes = () => new Set(DEFAULT_RECOVERY_DOMAIN_STATUS_CODES);

const normalizeRecoveryDomainStatusCodes = (statusCodes: unknown) => {
  const values = Array.isArray(statusCodes)
    ? statusCodes
    : typeof statusCodes === 'string'
      ? statusCodes.split(/[,，\r\n]+/)
      : [];
  const normalized = new Set<number>();

  values.forEach((value) => {
    const statusCode = Number(String(value).trim());
    if (Number.isInteger(statusCode) && statusCode >= 100 && statusCode <= 599) {
      normalized.add(statusCode);
    }
  });

  return normalized;
};

const fetchUploadErrorPromptResponse = async () => {
  if (typeof fetch !== 'function') {
    throw new Error('Fetch is unavailable');
  }

  const controller = typeof AbortController !== 'undefined' ? new AbortController() : undefined;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<Response>((_, reject) => {
    timeoutId = setTimeout(() => {
      controller?.abort();
      reject(new Error('Upload error prompt config request timed out'));
    }, UPLOAD_ERROR_PROMPT_FETCH_TIMEOUT_MS);
  });

  try {
    return await Promise.race([
      fetch(UPLOAD_ERROR_PROMPT_CONFIG_URL, {
        method: 'GET',
        credentials: 'include',
        cache: 'no-store',
        signal: controller?.signal,
      }),
      timeoutPromise,
    ]);
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
};

const requestUploadErrorPromptStatusCodes = async () => {
  const response = await fetchUploadErrorPromptResponse();
  if (!response.ok) {
    throw new Error(`Failed to fetch upload error prompt config: ${response.status}`);
  }

  const result = (await response.json()) as ApiResponse<UploadErrorPromptConfig>;
  const ok = result.success === true || result.isSuccess === true;
  const statusCodes = normalizeRecoveryDomainStatusCodes(
    (result.data || result.detail)?.recoveryDomainStatusCodes
  );
  if (!ok || statusCodes.size === 0) {
    throw new Error(result.message || 'Invalid upload error prompt config');
  }

  return statusCodes;
};

export const clearUploadErrorPromptConfigCache = () => {
  cachedRecoveryDomainStatusCodes = null;
  cachedRecoveryDomainStatusCodesExpiresAt = 0;
  inflightUploadErrorPromptRequest = null;
};

export const fetchUploadErrorPromptStatusCodes = async () => {
  const now = Date.now();
  if (cachedRecoveryDomainStatusCodes && now < cachedRecoveryDomainStatusCodesExpiresAt) {
    return new Set(cachedRecoveryDomainStatusCodes);
  }

  if (!inflightUploadErrorPromptRequest) {
    inflightUploadErrorPromptRequest = requestUploadErrorPromptStatusCodes()
      .catch((error) => {
        console.warn('Failed to load upload error prompt config, using defaults:', error);
        return getDefaultRecoveryDomainStatusCodes();
      })
      .then((statusCodes) => {
        cachedRecoveryDomainStatusCodes = new Set(statusCodes);
        cachedRecoveryDomainStatusCodesExpiresAt = Date.now() + UPLOAD_ERROR_PROMPT_CACHE_TTL_MS;
        return statusCodes;
      })
      .finally(() => {
        inflightUploadErrorPromptRequest = null;
      });
  }

  return inflightUploadErrorPromptRequest.then((statusCodes) => new Set(statusCodes));
};

export const isUploadRecoveryDomainStatus = (
  httpStatus?: number | null,
  recoveryDomainStatusCodes: Iterable<number> = DEFAULT_RECOVERY_DOMAIN_STATUS_CODE_SET
) => {
  const statusCodeSet = recoveryDomainStatusCodes instanceof Set
    ? recoveryDomainStatusCodes
    : new Set(recoveryDomainStatusCodes);
  return typeof httpStatus === 'number' && statusCodeSet.has(httpStatus);
};

export const isNetworkUploadError = (errorMessage: string) => {
  return errorMessage.trim().toLowerCase() === 'network error';
};

const DOMESTIC_ANDROID_BROWSER_TOKENS = [
  'huaweibrowser',
  'miuibrowser',
  'heytapbrowser',
  'oppobrowser',
  'vivobrowser',
  'mqqbrowser',
  'ucbrowser',
  'ucbs',
  'quark',
  'baiduboxapp',
  'sogoumobilebrowser',
  'qihoobrowser',
  'qhbrowser',
  'liebaofast',
];

export const isDomesticAndroidBrowser = (
  userAgent = typeof navigator === 'undefined' ? '' : navigator.userAgent
) => {
  const normalizedUserAgent = userAgent.toLowerCase();
  if (!normalizedUserAgent.includes('android')) {
    return false;
  }

  // Edge for Android uses EdgA/, so it must not be told to switch to Edge.
  if (/edg(?:a|ios)?\//.test(normalizedUserAgent)) {
    return false;
  }

  return DOMESTIC_ANDROID_BROWSER_TOKENS.some((token) => normalizedUserAgent.includes(token));
};

const formatUploadFileSize = (fileSize?: number) => {
  if (typeof fileSize !== 'number' || !isFinite(fileSize) || fileSize < 0) {
    return '';
  }

  if (fileSize === 0) {
    return '0 B';
  }

  const unit = 1024;
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const unitIndex = Math.min(Math.floor(Math.log(fileSize) / Math.log(unit)), units.length - 1);
  const size = fileSize / Math.pow(unit, unitIndex);

  return `${parseFloat(size.toFixed(2))} ${units[unitIndex]}`;
};

export const showUploadErrorModal = async (
  t: (key: string, options?: any) => string,
  errorMessage: string,
  fileName: string,
  traceId?: string,
  fileSize?: number,
  httpStatus?: number
) => {
  const recoveryDomainStatusCodes = typeof httpStatus === 'number'
    ? await fetchUploadErrorPromptStatusCodes()
    : DEFAULT_RECOVERY_DOMAIN_STATUS_CODE_SET;
  const systemTitle = (window as any).systemTitle || t('system.title');
  const customerServiceEnabled = isCustomerServiceEnabledSync();
  const formattedFileSize = formatUploadFileSize(fileSize);
  const displayFileName = formattedFileSize ? `${fileName} (${formattedFileSize})` : fileName;
  const shouldShowNetworkRecovery = isNetworkUploadError(errorMessage);
  const shouldShowAndroidBrowserGuidance = shouldShowNetworkRecovery && isDomesticAndroidBrowser();
  const shouldShowRecoveryDomainPrompt = isUploadRecoveryDomainStatus(httpStatus, recoveryDomainStatusCodes);
  const openCustomerService = () => {
    const message = [
      '我上传文件失败，请帮我排查。',
      `文件名：${displayFileName}`,
      `错误信息：${errorMessage}`,
      traceId ? `Trace ID：${traceId}` : '',
    ].filter(Boolean).join('\n');

    window.dispatchEvent(new CustomEvent('imgcloud:open-support', {
      detail: {
        message,
        autoSend: true,
        context: {
          lastUploadError: {
            fileName,
            fileSize,
            errorMessage,
            traceId,
          },
        },
      },
    }));
    Modal.destroyAll();
  };

  const UploadErrorContent: React.FC = () => {
    const [nodeStatusOpen, setNodeStatusOpen] = useState(false);
    const openNodeStatus = () => setNodeStatusOpen(true);

    return (
      <div>
        <span>{t('upload.fileUploadFailed', { fileName: displayFileName })}</span>
        {shouldShowNetworkRecovery && (
          <Alert
            type="error"
            showIcon
            style={{
              marginTop: 12,
              marginBottom: 12,
              borderColor: '#ff4d4f',
              background: '#fff1f0',
            }}
            message={(
              <span style={{ color: '#a8071a', fontWeight: 700 }}>
                {t('upload.networkErrorSwitchTitle')}
              </span>
            )}
            description={(
              <Space direction="vertical" size={8}>
                <span>{t('upload.networkErrorSwitchDescription')}</span>
                <Button
                  danger
                  onClick={openNodeStatus}
                  type="primary"
                >
                  {t('upload.switchNode')}
                </Button>
              </Space>
            )}
          />
        )}
        {shouldShowAndroidBrowserGuidance && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message={t('upload.androidBrowserEdgeRetryTitle')}
            description={(
              <Space direction="vertical" size={8}>
                <span>{t('upload.androidBrowserEdgeRetryDescription')}</span>
                <Button onClick={openNodeStatus}>{t('upload.switchNode')}</Button>
              </Space>
            )}
          />
        )}
        {shouldShowRecoveryDomainPrompt && (
          <Alert
            type="warning"
            showIcon
            style={{
              marginTop: 12,
              marginBottom: 12,
              borderColor: '#faad14',
              background: '#fffbe6',
            }}
            message={(
              <span style={{ color: '#ad6800', fontWeight: 700 }}>
                {t('upload.recoveryDomainErrorTitle')}
              </span>
            )}
            description={(
              <Space direction="vertical" size={8}>
                <span>{t('upload.recoveryDomainErrorDescription', { status: httpStatus })}</span>
                <Button
                  onClick={openNodeStatus}
                  type="primary"
                >
                  {t('upload.switchNode')}
                </Button>
              </Space>
            )}
          />
        )}
        <Divider orientation={'left'}>
          <span style={{ fontSize: '12px' }}>{t('upload.errorDetails')}</span>
        </Divider>
        <span>{errorMessage}</span>
        {traceId && (
          <>
            <Divider orientation={'left'}>
              <span style={{ fontSize: '12px' }}>{t('upload.traceId')}</span>
            </Divider>
            <div>
              <Text code copyable>{traceId}</Text>
              <div style={{ marginTop: '6px', color: '#757D8B', fontSize: '12px' }}>
                {t('upload.traceIdHelp')}
              </div>
            </div>
          </>
        )}
        {customerServiceEnabled && (
          <>
            <Divider orientation={'left'}>
              <span style={{ fontSize: '12px' }}>智能客服</span>
            </Divider>
            <Space direction="vertical" size={6}>
              <span style={{ color: '#4C5362', fontSize: '12px' }}>
                可将文件名、错误信息和 Trace ID 自动带给图床小镇客服排查。
              </span>
              <Button type="primary" size="small" onClick={openCustomerService}>
                让客服帮我排查
              </Button>
            </Space>
          </>
        )}
        <Divider orientation={'left'}>
          <span style={{ fontSize: '12px' }}>{t('upload.buyVipTitle')}</span>
        </Divider>
        <span style={{ color: 'red' }}>
          1. <a href={'https://c.fakamiao.top/shopDetail/Wte5t6'} target={'_blank'} rel={'noreferrer'}>{t('upload.buyVipStep1')}</a>{' '}
          <a href={'https://www.zhufaka.cn/shop/R8QB4K01'} target={'_blank'} rel={'noreferrer'}>{t('upload.buyVipStep1Alt')}</a>
          2. {t('upload.buyVipStep2')}
          3. {t('upload.buyVipStep3', { systemTitle })} {t('login.login')}
          4. {t('upload.buyVipStep4')}
          5. {t('upload.buyVipStep5')}
        </span>
        <Divider orientation={'left'}>
          <span style={{ fontSize: '12px' }}>{t('upload.moreSupport')}</span>
        </Divider>
        <img
          style={{ margin: '0 auto', display: 'block' }}
          src={'https://z.wiki/autoupload/20250503/OG1U/1126X1494/mmqrcode1746280934239.png'}
          width={'257px'}
          height={'257px'}
          alt={t('upload.moreSupport')}
        />
        <NodeStatusModal open={nodeStatusOpen} onClose={() => setNodeStatusOpen(false)} />
      </div>
    );
  };

  Modal.error({
    content: <UploadErrorContent />,
    title: t('upload.uploadFailed'),
  });
};
